import {
  ClientResponse,
  ConversationActionParams,
  ConversationEventType,
  ConversationParticipant,
  SelectOptionAction,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessagePart,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ReportWarning, ErrorResponse, ApiMethods} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type ActionSendMessageResponse =
  | {type: 'success'; data: ConversationsApiDataConversation}
  | ErrorResponse;

type ActionHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
  ) => Promise<ActionSendMessageResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  reportWarning?: ReportWarning;
  getParticipant: () => ConversationParticipant | undefined;
};

export class ActionHandler {
  private readonly deps: ActionHandlerDeps;

  constructor(deps: ActionHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: ConversationActionParams): Promise<ClientResponse> => {
    const {action, conversationId} = params;

    if (action && action.name === 'select-option') {
      return this.handleSelectOption(
        conversationId,
        action as SelectOptionAction,
      );
    }

    return {code: 200};
  };

  private async handleSelectOption(
    conversationId: string,
    action: SelectOptionAction,
  ): Promise<ClientResponse> {
    const {message, selectedOptionId} = action;
    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const selectedOption = message.options.find(
      (option) => option.id === selectedOptionId,
    );
    if (!selectedOption) {
      this.deps.reportWarning?.('Unknown selected option', {
        tags: {
          selected_option_id: selectedOptionId,
        },
      });
      return {code: 400, error: `Unknown selected option: ${selectedOptionId}`};
    }

    const payload = ActionHandler.buildSelectedOptionPayload({
      action,
      participant,
      selectedOption,
    });
    const sendStartedAt = Date.now();

    const response = await this.deps.sendMessage(conversationId, payload);
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const {conversation, newMessages} = FrameworkAdapter.diffConversation({
      previousMessageIds: [message.id, payload.id].filter(
        (id): id is string => id !== undefined,
      ),
      apiConversation: response.data,
      requestContext: {},
      user: participant,
      minimumTimestamp: sendStartedAt,
    });

    this.emitInputModeContextValues(
      conversationId,
      conversation.context.enabledInputModes,
    );

    newMessages.forEach((newMessage) => {
      this.deps.emitConversationEvent({
        name: 'conversation-message-added',
        conversationId,
        participantId: newMessage.participant.id,
        messageId: newMessage.id,
        nonce: newMessage.nonce,
        message: newMessage,
      });
    });

    return {code: 200};
  }

  private emitInputModeContextValues(
    conversationId: string,
    enabledInputModes: unknown,
  ): void {
    if (
      !Array.isArray(enabledInputModes) ||
      !enabledInputModes.every((mode) => typeof mode === 'string')
    ) {
      return;
    }

    this.deps.emitConversationEvent({
      name: 'conversation-set-context-values',
      conversationId,
      values: {enabledInputModes},
    });
  }

  private static buildSelectedOptionPayload({
    action,
    participant,
    selectedOption,
  }: {
    action: SelectOptionAction;
    participant: ConversationParticipant;
    selectedOption: SelectOptionAction['message']['options'][number];
  }): ConversationsApiSendMessageEndpointRequest {
    const creatorPartId = ActionHandler.buildCreatorPartId(action.message.id);

    return {
      id: ApiMethods.generateMessageId(),
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts: [ActionHandler.buildSelectedOptionPart(selectedOption)],
      ...(creatorPartId ? {creator_part_id: creatorPartId} : {}),
      page_context: ActionHandler.extractPageContext(action.data),
    };
  }

  private static buildCreatorPartId(messagePartId: string): string | undefined {
    const separatorIndex = messagePartId.lastIndexOf('.');
    if (separatorIndex <= 0 || separatorIndex === messagePartId.length - 1) {
      return undefined;
    }

    return messagePartId;
  }

  private static buildSelectedOptionPart(
    selectedOption: SelectOptionAction['message']['options'][number],
  ): ConversationsApiDataMessagePart {
    return {
      id: 1,
      type: 'selected_option',
      content: {
        id: selectedOption.id,
        text: selectedOption.label,
      },
    };
  }

  private static extractPageContext(
    data?: Record<string, unknown>,
  ): Record<string, string> | undefined {
    const candidate =
      (data?.page_context as Record<string, unknown> | undefined) ??
      (data?.pageContext as Record<string, unknown> | undefined);
    if (!candidate || typeof candidate !== 'object') {
      return undefined;
    }

    const pageContext = Object.fromEntries(
      Object.entries(candidate).map(([key, value]) => {
        const normalized =
          value &&
          typeof value === 'object' &&
          'value' in (value as Record<string, unknown>)
            ? (value as {value: unknown}).value
            : value;
        return [key, String(normalized)];
      }),
    );
    return Object.keys(pageContext).length > 0 ? pageContext : undefined;
  }
}
